import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { WorkspaceDto } from "@common/dto/workspace/workspace.dto";
import { PrepareCheckoutResponseDto } from "../dto/billing/flatRate/prepare-checkout-response.dto";
import { CalculatePriceDto } from "../dto/billing/flatRate/calculate-price.dto";
import {
    calculatePriceResponseSchema,
    EntityBillingCountResponseSchema,
    FlatRatePlanSchema,
} from "../schemas/billing.schema";
import { SubscribeDto } from "../dto/billing/flatRate/subscribe.dto";
import { UpgradeDto } from "../dto/billing/flatRate/upgrade.dto";
import { ConvertDto } from "../dto/billing/flatRate/convert.dto";
import { DowngradeDto } from "../dto/billing/flatRate/downgrade.dto";

const c = initContract();

export const flatRateBillingContract = c.router(
    {
        prepareWorkspaceCheckout: {
            method: "POST",
            path: "/workspace/:workspaceId/prepare-checkout",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: c.noBody(),
            responses: {
                200: c.type<PrepareCheckoutResponseDto>(),
            },
        },
        prepareOrganizationCheckout: {
            method: "POST",
            path: "/organization/:organizationId/prepare-checkout",
            pathParams: z.object({
                organizationId: z.string().uuid(),
            }),
            body: c.noBody(),
            responses: {
                200: c.type<PrepareCheckoutResponseDto>(),
            },
        },

        startEnterpriseTrial: {
            method: "POST",
            path: "/:workspaceId/enterprise-trial",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: c.noBody(),
            responses: {
                200: c.type<WorkspaceDto>(),
            },
        },
        calculatePrice: {
            method: "POST",
            path: "/:workspaceId/calculate-price",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: c.type<CalculatePriceDto>(),
            responses: {
                200: calculatePriceResponseSchema,
            },
        },
        subscribe: {
            method: "POST",
            path: "/:workspaceId/subscribe",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: c.type<SubscribeDto>(),
            responses: {
                200: c.type<WorkspaceDto>(),
            },
        },
        upgrade: {
            method: "POST",
            path: "/:workspaceId/upgrade",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: c.type<UpgradeDto>(),
            responses: {
                200: c.type<WorkspaceDto>(),
            },
        },
        convert: {
            method: "POST",
            path: "/:workspaceId/convert",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: c.type<ConvertDto>(),
            responses: {
                200: c.type<WorkspaceDto>(),
            },
        },
        downgrade: {
            method: "POST",
            path: "/:workspaceId/downgrade",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: c.type<DowngradeDto>(),
            responses: {
                200: c.noBody(),
            },
        },
        plans: {
            method: "GET",
            path: "/plans",
            responses: {
                200: z.array(FlatRatePlanSchema),
            },
        },
        upgradeToCatalogV3: {
            method: "POST",
            path: "/:workspaceId/upgrade-to-v3",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: c.noBody(),
            responses: {
                200: c.type<WorkspaceDto>(),
                403: c.noBody(),
                404: c.noBody(),
                409: c.noBody(),
            },
        },
        getEntityBillingCount: {
            method: "GET",
            path: "/:workspaceId/entity-billing-count",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                /** Entity count data for the Plus V2 metered plan. */
                200: EntityBillingCountResponseSchema,
                /** Workspace not found or user lacks access. */
                404: c.noBody(),
            },
        },
    },
    {
        pathPrefix: "/flat-rate-billing",
    },
);
